import {
  integerContent,
  type Uni,
  uniAdd,
  uniCoefficient,
  uniCommonDenominator,
  uniDegree,
  uniDivmod,
  uniEval,
  uniLeading,
  uniMul,
  uniScale,
  uniShift,
  uniSub,
  uniTrim,
} from "@/lib/tool-kit/algebra/uni";
import { absBig, BIG_ONE, gcdBig } from "@/lib/tool-kit/math/bigint";
import {
  addR,
  divR,
  fromBigInt,
  intR,
  isZeroR,
  makeRational,
  mulR,
  negR,
  RATIONAL_ZERO,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/** One round of polynomial long division. */
export interface DivisionRound {
  /** The leading term of what is left, divided by the divisor's leading term. */
  quotientTerm: Uni;
  /** quotientTerm × divisor, the thing subtracted. */
  product: Uni;
  /** What is left after subtracting. */
  remainder: Uni;
  /** Leading coefficient and degree of the dividend-so-far, for the written sentence. */
  leadingBefore: { coefficient: Rational; degree: number };
}

export interface LongDivision {
  quotient: Uni;
  remainder: Uni;
  rounds: DivisionRound[];
}

/** Polynomial long division with every round recorded. `divisor` must not be zero. */
export function longDivision(dividend: Uni, divisor: Uni): LongDivision {
  const d = uniTrim(divisor);
  const lead = uniLeading(d);
  let remainder = uniTrim(dividend);
  let quotient: Uni = [];
  const rounds: DivisionRound[] = [];
  while (remainder.length >= d.length && remainder.length > 0) {
    const degree = remainder.length - d.length;
    const factor = divR(uniLeading(remainder), lead);
    const quotientTerm = uniShift([factor], degree);
    const product = uniMul(quotientTerm, d);
    const leadingBefore = {
      coefficient: uniLeading(remainder),
      degree: remainder.length - 1,
    };
    remainder = uniSub(remainder, product);
    quotient = uniAdd(quotient, quotientTerm);
    rounds.push({ quotientTerm, product, remainder, leadingBefore });
  }
  return { quotient, remainder, rounds };
}

export interface HornerRow {
  /** The coefficients, highest degree first. */
  coefficients: Rational[];
  /** What is multiplied by `at` and carried: blank under the first coefficient. */
  products: (Rational | null)[];
  /** The running totals; the last is P(at). */
  sums: Rational[];
}

/** Synthetic division (Horner's scheme) of P by (x − at): the table and its last entry P(at). */
export function horner(p: Uni, at: Rational): HornerRow {
  const coefficients = [...uniTrim(p)].reverse();
  const products: (Rational | null)[] = [];
  const sums: Rational[] = [];
  let carry = RATIONAL_ZERO;
  coefficients.forEach((c, i) => {
    if (i === 0) {
      products.push(null);
      sums.push(c);
      carry = c;
      return;
    }
    const product = mulR(carry, at);
    products.push(product);
    carry = addR(c, product);
    sums.push(carry);
  });
  return { coefficients, products, sums };
}

/** A column-by-column sum or difference: P, Q and the result at each degree. */
export interface DegreeRow {
  degree: number;
  p: Rational;
  q: Rational;
  result: Rational;
}

export function columnTable(p: Uni, q: Uni, subtract: boolean): DegreeRow[] {
  const top = Math.max(uniDegree(p), uniDegree(q));
  const rows: DegreeRow[] = [];
  for (let degree = top; degree >= 0; degree -= 1) {
    const a = uniCoefficient(p, degree);
    const b = uniCoefficient(q, degree);
    rows.push({
      degree,
      p: a,
      q: b,
      result: subtract ? addR(a, negR(b)) : addR(a, b),
    });
  }
  return rows;
}

/** P × Q as one partial product per term of P, then the total. */
export interface PartialProduct {
  term: Uni;
  product: Uni;
}

export function partialProducts(p: Uni, q: Uni): PartialProduct[] {
  const parts: PartialProduct[] = [];
  for (let degree = uniDegree(p); degree >= 0; degree -= 1) {
    const c = uniCoefficient(p, degree);
    if (!isZeroR(c)) {
      const term = uniShift([c], degree);
      parts.push({ term, product: uniMul(term, q) });
    }
  }
  return parts;
}

// ----------------------------------------------------------------------------------------
// Roots
// ----------------------------------------------------------------------------------------

export interface Complex {
  re: number;
  im: number;
}

export interface RationalRoot {
  value: Rational;
  multiplicity: number;
}

export interface RootsResult {
  degree: number;
  rational: RationalRoot[];
  /** Roots that are not rational, as complex floats (conjugates appear as pairs). */
  numeric: Complex[];
  /** The part of P left after the rational roots were divided out. */
  remainder: Uni;
  /** False when the search for rational roots was cut short by large coefficients. */
  rationalComplete: boolean;
}

const MAX_CANDIDATES = 20_000;
const SAFE = BigInt(Number.MAX_SAFE_INTEGER);

function divisorsOf(n: bigint): bigint[] | null {
  if (n > SAFE) {
    return null;
  }
  const value = Number(n);
  const found: number[] = [];
  for (let d = 1; d * d <= value; d += 1) {
    if (value % d === 0) {
      found.push(d);
      if (d * d !== value) {
        found.push(value / d);
      }
    }
    if (found.length > 4000) {
      return null;
    }
  }
  return found.map((d) => BigInt(d));
}

function toIntegers(p: Uni): Uni {
  const scaled = uniScale(p, fromBigInt(uniCommonDenominator([p])));
  const content = integerContent(scaled);
  return uniScale(scaled, makeRational(BIG_ONE, content));
}

const GAVE_UP = Symbol("gave-up");

function findRationalRoot(
  p: Uni,
  divisorsLead: bigint[],
  divisorsTail: bigint[]
): Rational | null | typeof GAVE_UP {
  let tested = 0;
  for (const num of divisorsTail) {
    for (const den of divisorsLead) {
      if (gcdBig(num, den) !== BIG_ONE) {
        continue;
      }
      tested += 1;
      if (tested > MAX_CANDIDATES) {
        return GAVE_UP;
      }
      for (const sign of [BIG_ONE, -BIG_ONE]) {
        const candidate = makeRational(sign * num, den);
        if (isZeroR(uniEval(p, candidate))) {
          return candidate;
        }
      }
    }
  }
  return null;
}

export interface RootSearch {
  root: Rational | null;
  /** True when every candidate was tested, so a null root means there is none. */
  exhaustive: boolean;
}

/** One rational root of an integer-coefficient polynomial with a non-zero constant term. */
export function searchRationalRoot(p: Uni): RootSearch {
  const lead = divisorsOf(absBig(uniLeading(p).num));
  const tail = divisorsOf(absBig(p[0].num));
  if (lead === null || tail === null || isZeroR(p[0])) {
    return { root: null, exhaustive: false };
  }
  const found = findRationalRoot(p, lead, tail);
  return found === GAVE_UP
    ? { root: null, exhaustive: false }
    : { root: found, exhaustive: true };
}

function deflate(p: Uni, root: Rational): Uni {
  return uniDivmod(p, [negR(root), intR(1)]).quotient;
}

function cmul(a: Complex, b: Complex): Complex {
  return { re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re };
}

function cdiv(a: Complex, b: Complex): Complex {
  const d = b.re * b.re + b.im * b.im;
  return {
    re: (a.re * b.re + a.im * b.im) / d,
    im: (a.im * b.re - a.re * b.im) / d,
  };
}

function evalComplex(coeffs: number[], z: Complex): Complex {
  let result: Complex = { re: 0, im: 0 };
  for (let i = coeffs.length - 1; i >= 0; i -= 1) {
    const product = cmul(result, z);
    result = { re: product.re + coeffs[i], im: product.im };
  }
  return result;
}

/** Durand–Kerner iteration on a monic polynomial given lowest degree first. */
function durandKerner(coeffs: number[]): Complex[] {
  const n = coeffs.length - 1;
  const radius = 1 + Math.max(...coeffs.slice(0, n).map(Math.abs));
  const roots: Complex[] = Array.from({ length: n }, (_, k) => {
    const angle = (2 * Math.PI * k) / n + 0.4;
    return {
      re: radius * 0.6 * Math.cos(angle),
      im: radius * 0.6 * Math.sin(angle),
    };
  });
  for (let iteration = 0; iteration < 1000; iteration += 1) {
    let largest = 0;
    for (let i = 0; i < n; i += 1) {
      let denominator: Complex = { re: 1, im: 0 };
      for (let j = 0; j < n; j += 1) {
        if (j !== i) {
          denominator = cmul(denominator, {
            re: roots[i].re - roots[j].re,
            im: roots[i].im - roots[j].im,
          });
        }
      }
      const delta = cdiv(evalComplex(coeffs, roots[i]), denominator);
      roots[i] = { re: roots[i].re - delta.re, im: roots[i].im - delta.im };
      largest = Math.max(largest, Math.hypot(delta.re, delta.im));
    }
    if (largest < 1e-14 * radius) {
      break;
    }
  }
  return roots;
}

function cleanRoot(z: Complex): Complex {
  const scale = Math.max(1, Math.abs(z.re));
  return {
    re: Math.abs(z.re) < 1e-12 ? 0 : z.re,
    im: Math.abs(z.im) < 1e-9 * scale ? 0 : z.im,
  };
}

function sortRoots(roots: Complex[]): Complex[] {
  return [...roots].sort((a, b) => {
    if ((a.im === 0) !== (b.im === 0)) {
      return a.im === 0 ? -1 : 1;
    }
    return a.re - b.re || a.im - b.im;
  });
}

/** Roots of the part left over once rational roots are out: closed form to degree 2. */
function numericRoots(p: Uni): Complex[] {
  const degree = uniDegree(p);
  if (degree < 1) {
    return [];
  }
  const lead = toNumberR(uniLeading(p));
  const coeffs = uniTrim(p).map((c) => toNumberR(c) / lead);
  if (degree === 1) {
    return [{ re: 0 - coeffs[0], im: 0 }];
  }
  if (degree === 2) {
    const [c, b] = coeffs;
    const disc = b * b - 4 * c;
    if (disc >= 0) {
      const root = Math.sqrt(disc);
      return sortRoots([
        { re: (-b - root) / 2, im: 0 },
        { re: (-b + root) / 2, im: 0 },
      ]);
    }
    const imag = Math.sqrt(-disc) / 2;
    return [
      { re: 0 - b / 2, im: -imag },
      { re: 0 - b / 2, im: imag },
    ];
  }
  return sortRoots(durandKerner(coeffs).map(cleanRoot));
}

/** Rational roots exactly, the rest numerically. `p` must not be the zero polynomial. */
export function findRoots(p: Uni): RootsResult {
  let work = toIntegers(uniTrim(p));
  const degree = uniDegree(work);
  const rational: RationalRoot[] = [];
  let zeros = 0;
  while (work.length > 1 && isZeroR(work[0])) {
    work = work.slice(1);
    zeros += 1;
  }
  if (zeros > 0) {
    rational.push({ value: RATIONAL_ZERO, multiplicity: zeros });
  }
  let complete = true;
  while (uniDegree(work) >= 1) {
    const lead = divisorsOf(absBig(uniLeading(work).num));
    const tail = divisorsOf(absBig(work[0].num));
    if (lead === null || tail === null) {
      complete = false;
      break;
    }
    const root = findRationalRoot(work, lead, tail);
    if (root === GAVE_UP) {
      complete = false;
      break;
    }
    if (root === null) {
      break;
    }
    work = deflate(work, root);
    const known = rational.find(
      (r) => r.value.num === root.num && r.value.den === root.den
    );
    if (known) {
      known.multiplicity += 1;
    } else {
      rational.push({ value: root, multiplicity: 1 });
    }
  }
  rational.sort((a, b) => signR(addR(a.value, negR(b.value))));
  return {
    degree,
    rational,
    numeric: numericRoots(work),
    remainder: work,
    rationalComplete: complete,
  };
}
